import {LeanDocument, Types} from 'mongoose';
import {isMongoId} from "class-validator";

/**
 * Returns if given data is a valid ObjectId of mongoose.
 * Allows strings and numbers as valid input.
 * @param data The data to validate
 * @returns Is data valid ObjectId?
 * @author Joshua Seipel
 */
export function isObjectId(data: any): data is Types.ObjectId {
  try {
    return isMongoId(data);
  } catch (error) {
    return false;
  }
}

type Populated<T> = T extends Types.ObjectId | null | undefined ? never : T & { _id: Types.ObjectId };

export function isPopulated<T>(data: T): data is Populated<T> {
  return !!data && !isObjectId(data);
}

export function getPopulated<T>(data: T): Populated<T> | undefined {
  if (isPopulated(data)) {
    return data;
  }
  return undefined;
}

type RetrieveObjectIdReturnType<T> = T extends undefined ? Types.ObjectId | undefined : Types.ObjectId;

export function retrieveObjectId<T>(data: T): RetrieveObjectIdReturnType<T> {
  if (data === undefined) {
    return undefined as RetrieveObjectIdReturnType<T>;
  }
  if (isPopulated(data)) {
    return data._id as RetrieveObjectIdReturnType<T>;
  }
  return data as RetrieveObjectIdReturnType<T>;
}

type MatchType = Types.ObjectId | LeanDocument<any> | undefined;

/**
 * Compares the IDs of two objects to determine if they are equal.
 * Notice: This method may return false if both IDs are undefined.
 *
 * @param a - The first object containing an ID to compare.
 * @param b - The second object containing an ID to compare.
 * @return {boolean} Returns true if the IDs of the two objects are equal, otherwise false.
 */
export function compareIds(a: MatchType, b: MatchType): boolean {
    const idA = retrieveObjectId(a);
    const idB = retrieveObjectId(b);
    return new Types.ObjectId(idA).equals(new Types.ObjectId(idB));
}
